import { useState, useMemo } from 'react';
import { Search, Filter, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { YourCheckaNavigation } from '@/components/Navbar';
import SupportTicketDetailsModal from '@/components/modals/SupportTicketDetailsModal';
import { useGetAllTicketsQuery, useGetTicketStatisticsQuery } from '@/store/api/apiSlice';
import { SupportTicket, TicketStatus, TicketPriority } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

type FilterType = 'all' | 'open' | 'in_progress' | 'waiting_on_employee' | 'resolved' | 'closed';
type PriorityFilterType = 'all' | 'low' | 'medium' | 'high' | 'urgent';

export default function Support() {
  const currentUser = useAppSelector(selectCurrentUser);
  const [statusFilter, setStatusFilter] = useState<FilterType>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Fetch tickets with filters
  const queryParams = useMemo(() => {
    const params: { status?: TicketStatus; priority?: TicketPriority } = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter as TicketStatus;
    }
    if (priorityFilter !== 'all') {
      params.priority = priorityFilter as TicketPriority;
    }
    return params;
  }, [statusFilter, priorityFilter]);

  const { data: tickets = [], isLoading } = useGetAllTicketsQuery(queryParams);
  const { data: stats } = useGetTicketStatisticsQuery();

  // Filter by search
  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return tickets;
    return tickets.filter((ticket) => {
      const userName = `${ticket.user?.firstName ?? ''} ${ticket.user?.lastName ?? ''}`.toLowerCase();
      const employeeName = (ticket.employeeName ?? '').toLowerCase();
      const employeeEmail = (ticket.employeeEmail ?? '').toLowerCase();
      const userEmail = (ticket.user?.email ?? '').toLowerCase();
      const supportCode = (ticket.supportCode ?? '').toLowerCase();
      return (
        ticket.subject.toLowerCase().includes(q) ||
        ticket.message.toLowerCase().includes(q) ||
        userEmail.includes(q) ||
        userName.includes(q) ||
        employeeName.includes(q) ||
        employeeEmail.includes(q) ||
        supportCode.includes(q)
      );
    });
  }, [tickets, searchQuery]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleRowSelect = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.OPEN:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case TicketStatus.IN_PROGRESS:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case TicketStatus.RESOLVED:
        return 'bg-green-50 text-green-700 border-green-100';
      case TicketStatus.CLOSED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriority.LOW:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      case TicketPriority.MEDIUM:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case TicketPriority.HIGH:
        return 'bg-orange-50 text-orange-700 border-orange-100';
      case TicketPriority.URGENT:
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const columns: Column<SupportTicket>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (_item, index) => (
        <span className="text-sm text-gray-700">
          {(currentPage - 1) * rowsPerPage + (index || 0) + 1}
        </span>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 font-medium truncate">{item.subject}</p>
        </div>
      ),
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      hasMenu: true,
      render: (item) => {
        const userName = `${item.user?.firstName ?? ''} ${item.user?.lastName ?? ''}`.trim();
        const display = userName || item.employeeName || '—';
        return (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">{display}</span>
            {item.supportCode && (
              <span className="text-[11px] font-mono text-gray-500 tracking-[0.04em]">
                {item.supportCode}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 rounded-lg font-medium capitalize"
        >
          {item.category}
        </Badge>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${getPriorityColor(item.priority)} px-3 py-1 rounded-lg font-medium capitalize`}
        >
          {item.priority}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${getStatusColor(item.status)} px-3 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">
          {new Date(item.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <Button
          className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-xl text-sm font-medium"
          onClick={() => setSelectedTicket(item)}
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  if (!currentUser) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading support tickets...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <YourCheckaNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={`${currentUser.firstName} ${currentUser.lastName}`}
          userInitials={getInitials(currentUser.firstName, currentUser.lastName)}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-auto p-8 md:p-10">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Support Tickets</h1>
            <p className="text-gray-600">Manage and respond to support requests</p>
          </div>

          {/* Statistics Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-sm text-gray-600 mb-1">Total Tickets</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalTickets}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-sm text-blue-600 mb-1">Active Tickets</p>
                <p className="text-3xl font-bold text-blue-900">{stats.activeTickets}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-6">
                <p className="text-sm text-red-600 mb-1">Urgent Tickets</p>
                <p className="text-3xl font-bold text-red-900">{stats.urgentTickets}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-6">
                <p className="text-sm text-green-600 mb-1">Resolved Tickets</p>
                <p className="text-3xl font-bold text-green-900">{stats.resolvedTickets}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by subject, user, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FilterType)}
                className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_on_employee">Waiting on Employee</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as PriorityFilterType)}
                className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="all">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="border border-gray-100 rounded-2xl overflow-hidden mb-6">
            {paginatedData.length > 0 ? (
              <DataTable
                data={paginatedData}
                columns={columns}
                showCheckbox={true}
                selectedRows={selectedRows}
                onRowSelect={handleRowSelect}
                hoverable={true}
                striped={true}
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">No tickets found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {paginatedData.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ‹
            </button>

            {currentPage > 2 && (
              <>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  1
                </button>
                {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
              </>
            )}

            {currentPage > 1 && (
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {currentPage - 1}
              </button>
            )}

            <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
              {currentPage}
            </button>

            {currentPage < totalPages && (
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {currentPage + 1}
              </button>
            )}

            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ›
            </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Support Ticket Details Modal */}
      <SupportTicketDetailsModal
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        ticket={selectedTicket}
      />
    </main>
  );
}
